
import { Component, Prop, Watch } from "vue-property-decorator"
import BaseInput from "~/core/BaseInput"
import {
  buildValidateScript,
  createValidateScriptRuleItem,
  hydrateValidateScriptRules,
  parseValidateScript,
  validateScriptRuleGroups,
} from "~/validate-script/core"
import type { ValidateScriptRuleItem } from "~/validate-script/core"

@Component
export default class ValidateScript extends BaseInput {
  @Prop(Boolean) readonly disabled!: boolean

  showDialog = false
  codeView = false
  codeDraft = ""
  validationError = ""
  ruleItems: ValidateScriptRuleItem[] = []
  selectedIndex = -1
  showAddDialog = false
  selectedCommandGroupIndex = 0
  commandSearch = ""

  get commandGroups() {
    return validateScriptRuleGroups
  }

  get selectedCommandGroup() {
    return this.commandGroups[this.selectedCommandGroupIndex] || this.commandGroups[0]
  }

  get isSearchingCommand(): boolean {
    return (this.commandSearch || "").trim().length > 0
  }

  get displayedCommands(): Array<{ cmd: string; description: string; group: string }> {
    const search = (this.commandSearch || "").trim().toLowerCase()
    if (!search) {
      return (this.selectedCommandGroup?.commands || []).map((command) => ({
        ...command,
        group: this.selectedCommandGroup.group,
      }))
    }

    return this.commandGroups
      .flatMap((group) =>
        group.commands.map((command) => ({
          ...command,
          group: group.group,
          commandRank: command.cmd.toLowerCase().includes(search) ? 0 : 1,
        }))
      )
      .filter(
        (command) =>
          command.commandRank === 0 ||
          command.description.toLowerCase().includes(search) ||
          command.group.toLowerCase().includes(search)
      )
      .sort((a, b) => a.commandRank - b.commandRank || a.cmd.localeCompare(b.cmd))
      .map(({ commandRank, ...command }) => command)
  }

  get selectedRule() {
    return this.ruleItems[this.selectedIndex] || null
  }

  get code() {
    return buildValidateScript(this.ruleItems)
  }

  @Watch("showDialog")
  onShowDialog(newValue: boolean) {
    if (newValue) {
      this.syncFromValue()
    } else {
      this.codeView = false
      this.validationError = ""
    }
  }

  syncFromValue() {
    this.validationError = ""
    this.codeDraft = this.value || ""
    try {
      this.ruleItems = hydrateValidateScriptRules(this.codeDraft)
    } catch {
      this.ruleItems = []
    }
    this.selectedIndex = this.ruleItems.length > 0 ? 0 : -1
  }

  openEditor() {
    if (this.disabled) {
      return
    }
    this.showDialog = true
  }

  openValidateScriptDocs() {
    const lang =
      typeof window !== "undefined" && window.navigator.language?.toLowerCase().startsWith("th")
        ? "th"
        : "en"
    const docsUrl = `/marketing/docs?collection=program-documentation&lang=${lang}&doc=validate-script`

    window.open(docsUrl, "_blank", "noopener")
  }

  openAddCommandDialog() {
    this.selectedCommandGroupIndex = Math.min(
      this.selectedCommandGroupIndex,
      this.commandGroups.length - 1
    )
    this.commandSearch = ""
    this.showAddDialog = true
  }

  selectRule(index: number) {
    this.selectedIndex = index
  }

  addRule(name: string) {
    const rule = createValidateScriptRuleItem(name)
    if (!rule) {
      return
    }
    this.ruleItems.push(rule)
    this.selectedIndex = this.ruleItems.length - 1
    this.showAddDialog = false
  }

  removeRule(id: string) {
    this.ruleItems = this.ruleItems.filter((rule) => rule.id !== id)
    this.selectedIndex = this.ruleItems.length > 0 ? Math.min(this.selectedIndex, this.ruleItems.length - 1) : -1
  }

  moveRule(index: number, direction: -1 | 1) {
    const target = index + direction
    if (target < 0 || target >= this.ruleItems.length) {
      return
    }
    const next = [...this.ruleItems]
    const [rule] = next.splice(index, 1)
    if (!rule) {
      return
    }
    next.splice(target, 0, rule)
    this.ruleItems = next
    this.selectedIndex = target
  }

  addParam() {
    if (!this.selectedRule) {
      return
    }
    this.selectedRule.params.push({
      label: this.selectedRule.variadicLabel || "Value",
      type: this.selectedRule.params[0]?.type || "string",
      value: "",
    })
  }

  removeParam(index: number) {
    if (!this.selectedRule) {
      return
    }
    this.selectedRule.params.splice(index, 1)
  }

  toggleCodeView() {
    if (this.codeView) {
      try {
        parseValidateScript(this.codeDraft)
        this.ruleItems = hydrateValidateScriptRules(this.codeDraft)
        this.selectedIndex = this.ruleItems.length > 0 ? 0 : -1
        this.validationError = ""
        this.codeView = false
      } catch (error) {
        this.validationError = error instanceof Error ? error.message : String(error)
      }
      return
    }

    this.codeDraft = this.code
    this.validationError = ""
    this.codeView = true
  }

  renderRulePreview(rule: ValidateScriptRuleItem) {
    return buildValidateScript([rule])
  }

  clickDiscard() {
    this.showDialog = false
  }

  clickSave() {
    const nextValue = this.codeView ? this.codeDraft.trim() : this.code
    try {
      parseValidateScript(nextValue)
      this.validationError = ""
      this.onInput(nextValue)
      this.showDialog = false
    } catch (error) {
      this.validationError = error instanceof Error ? error.message : String(error)
    }
  }
}
